import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Minus, Send, User, Bot, Check } from 'lucide-react';
import { sendChatMessage } from '@/lib/chatApi';
import { registerClient } from '@/lib/registerApi';
import { informacionPlanCompensacion } from '@/data/datos';
import { formatPhone } from '@/lib/utils';
import type { Embajador } from '@/types';

interface ChatbotProps {
  embajadorName?: string;
  ambassador?: Embajador | null;
}

type ChatStep = 'menu' | 'registro-curp' | 'registro-nombre' | 'registro-direccion' | 'registro-telefono' | 'registro-email' | 'registro-completado' | 'plan' | 'agente';

interface ChatMessage {
  id: string;
  tipo: 'bot' | 'usuario' | 'opciones';
  contenido: React.ReactNode;
  opciones?: string[];
}

interface FormData {
  curp: string;
  nombre_completo: string;
  direccion: string;
  email: string;
  whatsapp_number: string;
}

export default function Chatbot({ embajadorName, ambassador }: ChatbotProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [step, setStep] = useState<ChatStep>('menu');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [formData, setFormData] = useState<FormData>({
    curp: '',
    nombre_completo: '',
    direccion: '',
    email: '',
    whatsapp_number: ''
  });
  const [agenteInput, setAgenteInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      setTimeout(() => scrollToBottom(), 100);
    }
  }, [isMinimized, isOpen]);

  useEffect(() => {
    // Generate or retrieve persistent userId
    let storedUserId = localStorage.getItem('chat_user_id');
    if (!storedUserId) {
      storedUserId = crypto.randomUUID();
      localStorage.setItem('chat_user_id', storedUserId);
    }
  }, []);

  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
      setIsMinimized(false);
    };

    window.addEventListener('open-chatbot', handleOpenChat);

    return () => {
      window.removeEventListener('open-chatbot', handleOpenChat);
    };
  }, []);

  // Get ambassador slug from prop
  const getSlug = () => {
    return ambassador?.slug;
  };

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addBotMessage(
        `¡Hola! Soy tu asistente virtual de Volando Alto. ¿En qué puedo ayudarte hoy?`,
        ['¡Quiero ser embajador!', 'Ver plan de compensación', 'Preguntar al Agente']
      );
    }
  }, [isOpen]);

  const addBotMessage = (contenido: React.ReactNode, opciones?: string[]) => {
    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      tipo: 'bot',
      contenido,
      opciones
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const addUserMessage = (contenido: string) => {
    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      tipo: 'usuario',
      contenido
    };
    setMessages((prev) => [...prev, newMessage]);
  };

  const validateCURP = (curp: string): boolean => {
    // Función para validar una CURP
    function curpValida(curp: string): boolean {
      var re = /^([A-Z][AEIOUX][A-Z]{2}\d{2}(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\d|3[01])[HM](?:AS|B[CS]|C[CLMSH]|D[FG]|G[TR]|HG|JC|M[CNS]|N[ETL]|OC|PL|Q[TR]|S[PLR]|T[CSL]|VZ|YN|ZS)[B-DF-HJ-NP-TV-Z]{3}[A-Z\d])(\d)$/,
        validado = curp.match(re);

      if (!validado)  // Coincide con el formato general?
        return false;

      // Validar que coincida el dígito verificador
      function digitoVerificador(curp17: string): number {
        // Fuente https://consultas.curp.gob.mx/CurpSP/
        var diccionario = "0123456789ABCDEFGHIJKLMNÑOPQRSTUVWXYZ",
          lngSuma = 0.0,
          lngDigito = 0.0;
        for (var i = 0; i < 17; i++)
          lngSuma = lngSuma + diccionario.indexOf(curp17.charAt(i)) * (18 - i);
        lngDigito = 10 - lngSuma % 10;
        if (lngDigito == 10) return 0;
        return lngDigito;
      }

      if (validado[2] != String(digitoVerificador(validado[1])))
        return false;

      return true; // Validado
    }

    return curpValida(curp.toUpperCase());
  };

  const handleClose = () => {
    setIsOpen(false);
    setIsMinimized(false);
    // Reset state after closing animation
    setTimeout(() => {
      setMessages([]);
      setStep('menu');
      setFormData({
        curp: '',
        nombre_completo: '',
        direccion: '',
        email: '',
        whatsapp_number: ''
      });
    }, 500);
  };

  const handleMinimize = () => {
    setIsMinimized(true);
  };

  const handleMaximize = () => {
    setIsMinimized(false);
  };

  const handleOptionClick = (opcion: string) => {
    addUserMessage(opcion);

    switch (opcion) {
      case '¡Quiero ser embajador!':
        setStep('registro-curp');
        setTimeout(() => {
          addBotMessage(
            '¡Excelente decisión! Para comenzar tu registro como embajador Ismerely, necesito validar tu CURP.\n\nPor favor, ingresa tu CURP (18 caracteres):'
          );
        }, 500);
        break;

      case 'Ver plan de compensación':
        setStep('plan');
        setTimeout(() => {
          addBotMessage(
            <div className="space-y-4">
              <div className="font-medium text-[#8abd31]">📋 Plan de Compensación Ismerely</div>
              <div className="text-sm whitespace-pre-line bg-[#f8f9f5] p-3 rounded-lg">
                {informacionPlanCompensacion}
              </div>
            </div>,
            ['¡Quiero registrarme!', 'Menú principal']
          );
        }, 500);
        break;

      case 'Preguntar al Agente':
      case 'Preguntar al Agente tus dudas':
        setStep('agente');
        setTimeout(() => {
          addBotMessage(
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Bot className="w-5 h-5 text-[#8abd31]" />
                <span className="font-medium">Agente IA de Ismerely</span>
              </div>
              <p className="text-sm">
                Estoy aquí para resolver tus dudas sobre el plan de compensación, productos o el programa de embajadores.
                Pregúntame lo que necesites saber.
              </p>
            </div>
          );
        }, 500);
        break;

      case '¡Quiero registrarme!':
        setStep('registro-curp');
        setTimeout(() => {
          addBotMessage(
            '¡Perfecto! Vamos a comenzar tu registro. Por favor, ingresa tu CURP (18 caracteres):'
          );
        }, 500);
        break;

      case 'Menú principal':
        setStep('menu');
        setTimeout(() => {
          addBotMessage(
            '¿En qué más puedo ayudarte?',
            ['¡Quiero ser embajador!', 'Ver plan de compensación', 'Preguntar al Agente tus dudas']
          );
        }, 500);
        break;

      case 'Notificar al Embajador':
        {
          const phoneToContact = ambassador?.whatsapp_number || '524492109730';
          const message = `Hola ${embajadorName}, soy ${formData.nombre_completo}. Me acabo de registrar en la plataforma de Volando Alto Ismerely.\n\n` +
                          `*Mis datos de registro son:*\n` +
                          `• CURP: ${formData.curp}\n` +
                          `• Nombre completo: ${formData.nombre_completo}\n` +
                          `• Dirección: ${formData.direccion}\n` +
                          `• Teléfono: ${formatPhone(formData.whatsapp_number)}\n` +
                          `• Correo electrónico: ${formData.email}\n\n` +
                          `¿Podrías apoyarme con los siguientes pasos?`;
          
          const whatsappMessage = encodeURIComponent(message);
          window.open(`https://wa.me/${phoneToContact.toString().replace(/\D/g, '')}?text=${whatsappMessage}`, '_blank');
        }
        break;

      case 'Contactar Embajador':
        {
          const phoneToContact = ambassador?.whatsapp_number || '524492109730';
          let message = "";
          
          if (step === 'agente') {
            // Viene de un error en el Agente de IA
            message = `Hola, estoy intentando consultar con el asistente de IA pero no está disponible en este momento. Quisiera hacerte una consulta directa.`;
          } 
          else {
            // Error general en el proceso de registro
            message = `Hola, mi nombre es ${formData.nombre_completo}. Estoy intentando registrarme como embajador Ismerely pero el sistema presentó un error al procesar mis datos. ¿Podrías apoyarme con mi registro?\n\n` +
                      `*Mis datos:*\n` +
                      `• CURP: ${formData.curp}\n` +
                      `• Nombre completo: ${formData.nombre_completo}\n` +
                      `• Dirección: ${formData.direccion || 'No proporcionada'}\n` +
                      `• Teléfono: ${formatPhone(formData.whatsapp_number)}\n` +
                      `• Correo electrónico: ${formData.email}`;
          }
          
          const whatsappMessage = encodeURIComponent(message);
          window.open(`https://wa.me/${phoneToContact.toString().replace(/\D/g, '')}?text=${whatsappMessage}`, '_blank');
        }
        break;

      default:
        break;
    }
  };

  const handleSendMessage = async () => {
    if (!agenteInput.trim()) return;

    const userPrompt = agenteInput;
    addUserMessage(userPrompt);
    setAgenteInput('');

    // Create a placeholder bot message for streaming
    const botMsgId = Date.now().toString();
    const newBotMessage: ChatMessage = {
      id: botMsgId,
      tipo: 'bot',
      contenido: <span className="animate-pulse">...</span>,
      opciones: []
    };

    setMessages((prev) => [...prev, newBotMessage]);

    try {
      let currentText = "";
      const userId = localStorage.getItem('chat_user_id') || crypto.randomUUID();

      await sendChatMessage(userPrompt, userId, (chunk) => {
        currentText += chunk;

        // Remove <think>...</think> blocks for the user display
        // Standard DeepSeek R1 behavior puts <think> at the start.
        let displayContent = currentText.replace(/<think>[\s\S]*?<\/think>/g, "").trim();

        // If we are still "thinking" (open tag exists but no closing tag), show a status
        if (currentText.includes("<think>") && !currentText.includes("</think>")) {
          displayContent = "🧠 Analizando tu respuesta...";
        } else if (displayContent === "") {
          // If we finished thinking but haven't streamed text yet
          displayContent = "📝 Escribiendo...";
        }

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === botMsgId
              ? { ...msg, contenido: displayContent, opciones: [] } // Update content
              : msg
          )
        );
      }, "affiliate");

      // Add options after streaming completes
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMsgId
            ? { ...msg, opciones: ['¡Quiero ser embajador!', 'Menú principal'] }
            : msg
        )
      );

    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMsgId
            ? { ...msg, contenido: "Lo siento, el servicio de asesoría por IA no está disponible en este momento. ¿Te gustaría contactar directamente a tu embajador?", opciones: ['Contactar Embajador', 'Menú principal'] }
            : msg
        )
      );
    }
  };

  const handleFormSubmit = (value: string) => {
    addUserMessage(value);

    switch (step) {
      case 'registro-curp':
        if (validateCURP(value)) {
          setFormData((prev) => ({ ...prev, curp: value.toUpperCase() }));
          setStep('registro-nombre');
          setTimeout(() => {
            addBotMessage('✅ CURP válido. Ahora, por favor ingresa tu nombre completo:');
          }, 500);
        } else {
          setTimeout(() => {
            addBotMessage('❌ CURP inválido. Por favor, verifica e ingresa un CURP válido (18 caracteres):');
          }, 500);
        }
        break;

      case 'registro-nombre':
        if (value.trim().length >= 5) {
          setFormData((prev) => ({ ...prev, nombre_completo: value }));
          setStep('registro-direccion');
          setTimeout(() => {
            addBotMessage('Gracias. Ahora ingresa tu dirección completa (Calle, No. interior/exterior, Colonia, Ciudad, Estado, Código postal):');
          }, 500);
        } else {
          setTimeout(() => {
            addBotMessage('Por favor, ingresa un nombre válido (mínimo 5 caracteres):');
          }, 500);
        }
        break;

      case 'registro-direccion':
        if (value.trim().length >= 10) {
          setFormData((prev) => ({ ...prev, direccion: value }));
          setStep('registro-telefono');
          setTimeout(() => {
            addBotMessage('Excelente. Ahora, por favor ingresa tu número de teléfono (WhatsApp):');
          }, 500);
        } else {
          setTimeout(() => {
            addBotMessage('Por favor, ingresa una dirección válida (mínimo 10 caracteres):');
          }, 500);
        }
        break;

      case 'registro-telefono':
        {
          const phoneRegex = /^[0-9]{10,13}$/;
          const cleanPhone = value.replace(/\D/g, '');

          if (phoneRegex.test(cleanPhone)) {
            setFormData((prev) => ({ ...prev, whatsapp_number: cleanPhone }));
            setStep('registro-email');
            setTimeout(() => {
              addBotMessage('Perfecto. Por último, ingresa tu correo electrónico:');
            }, 500);
          } else {
            setTimeout(() => {
              addBotMessage('Número inválido. Por favor, ingresa un número de 10 dígitos:');
            }, 500);
          }
        }
        break;

      case 'registro-email':
        {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (emailRegex.test(value)) {
            const finalEmail = value;
            setFormData((prev) => ({ ...prev, email: finalEmail }));

            // Show loading message
            addBotMessage(<div className="flex items-center gap-2"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>Registrando tus datos...</div>);

            // Proceed to register with backend
            const registrationData = {
              nombre_completo: formData.nombre_completo,
              direccion: formData.direccion,
              curp: formData.curp,
              email: finalEmail,
              whatsapp_number: formData.whatsapp_number,
              slug: getSlug()
            };

            registerClient(registrationData)
              .then(() => {
                setStep('registro-completado');

                setTimeout(() => {
                  addBotMessage(
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-[#8abd31]">
                        <Check className="w-5 h-5" />
                        <span className="font-medium">¡Registro completado exitosamente!</span>
                      </div>
                      <div className="bg-[#f8f9f5] p-4 rounded-lg space-y-2">
                        <div className="text-sm">
                          <span className="font-medium">CURP:</span> {registrationData.curp}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Nombre completo:</span> {registrationData.nombre_completo}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Dirección:</span> {registrationData.direccion}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Teléfono:</span> {formatPhone(formData.whatsapp_number)}
                        </div>
                        <div className="text-sm">
                          <span className="font-medium">Correo electrónico:</span> {registrationData.email}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600">
                        Tu información ha sido enviada al embajador. Haz clic en el botón de abajo para notificarle directamente.
                      </p>
                    </div>,
                    ['Notificar al Embajador', 'Menú principal']
                  );
                }, 500);
              })
              .catch((error) => {
                console.error("Registration error:", error);
                let errorMsg = "Hubo un error al registrar tus datos. Por favor intenta de nuevo.";
                let options = ['Contactar Embajador', 'Menú principal'];

                if (error.message.includes("ya está registrado") || error.message.includes("409")) {
                  errorMsg = "⚠️ Este CURP ya está registrado. Para más información contacta a un embajador";
                }

                setTimeout(() => {
                  addBotMessage(errorMsg, options);
                  setStep('menu');
                }, 500);
              });

          } else {
            setTimeout(() => {
              addBotMessage('Correo electrónico inválido. Por favor, ingresa un email válido:');
            }, 500);
          }
        }
        break;

      default:
        break;
    }
  };

  return (
    <>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 z-50 p-4 rounded-full shadow-xl transition-all duration-300 bg-gradient-to-r from-[#e91e8c] to-[#f7bedd] hover:shadow-2xl hover:scale-110"
          aria-label="Abrir chatbot"
        >
          <MessageCircle className="w-6 h-6 text-white" />
        </button>
      )}

      {/* Minimized Chat Bar */}
      {isOpen && isMinimized && (
        <div
          onClick={handleMaximize}
          className="fixed bottom-6 right-6 z-50 w-[280px] bg-gradient-to-r from-[#e91e8c] to-[#f7bedd] rounded-full shadow-xl p-4 cursor-pointer hover:shadow-2xl transition-all duration-300 flex items-center justify-between"
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center overflow-hidden p-1">
              <img src="/images/Logo_Asistente_Volando_Alto.png" alt="Asistente" className="w-full h-full object-contain" />
            </div>
            <span className="text-white font-medium">Asistente Volando Alto</span>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
          >
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && !isMinimized && (
        <div className="fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] max-h-[calc(100vh-100px)] bg-white rounded-2xl shadow-2xl overflow-hidden animate-slide-up flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#8abd31] via-[#8abd31] to-[#e91e8c] p-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center overflow-hidden p-1.5">
                  <img src="/images/Logo_Asistente_Volando_Alto.png" alt="Asistente" className="w-full h-full object-contain" />
                </div>
                <div>
                  <div className="font-medium text-white">Asistente Volando Alto</div>
                  <div className="text-xs text-white/80">En línea</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Minimize Button */}
                <button
                  onClick={handleMinimize}
                  className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  title="Minimizar"
                >
                  <Minus className="w-4 h-4 text-white" />
                </button>
                {/* Close Button */}
                <button
                  onClick={handleClose}
                  className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  title="Cerrar"
                >
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 min-h-[250px] max-h-[50vh]">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.tipo === 'usuario' ? 'justify-end' : 'justify-start'
                  }`}
              >
                {message.tipo === 'bot' && (
                  <div className="w-8 h-8 bg-[#8abd31] rounded-full flex items-center justify-center flex-shrink-0 mr-2 overflow-hidden p-1">
                    <img src="/images/Logo_Asistente_Volando_Alto.png" alt="Bot" className="w-full h-full object-contain" />
                  </div>
                )}
                <div
                  className={`max-w-[80%] ${message.tipo === 'usuario'
                    ? 'bg-[#8abd31] text-white rounded-2xl rounded-tr-sm'
                    : 'bg-white shadow-sm rounded-2xl rounded-tl-sm'
                    } p-3`}
                >
                  <div className="text-sm">{message.contenido}</div>
                  {message.opciones && (
                    <div className="mt-3 space-y-2">
                      {message.opciones.map((opcion) => (
                        <button
                          key={opcion}
                          onClick={() => handleOptionClick(opcion)}
                          className="w-full text-left px-3 py-2 bg-white hover:bg-[#8abd31] hover:text-white rounded-lg text-sm transition-all duration-200 border border-[#8abd31] shadow-sm"
                        >
                          {opcion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {message.tipo === 'usuario' && (
                  <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center flex-shrink-0 ml-2">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          {(step === 'registro-curp' ||
            step === 'registro-nombre' ||
            step === 'registro-direccion' ||
            step === 'registro-email' ||
            step === 'registro-telefono' ||
            step === 'agente') && (
              <div className="p-3 bg-white border-t border-gray-100 flex-shrink-0">
                <div className="flex gap-2">
                  {step === 'agente' ? (
                    <>
                      <input
                        type="text"
                        value={agenteInput}
                        onChange={(e) => setAgenteInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                        placeholder="Escribe tu pregunta..."
                        className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm outline-none focus:ring-2 focus:ring-[#e91e8c]"
                      />
                      <button
                        onClick={handleSendMessage}
                        className="p-2 bg-[#e91e8c] text-white rounded-full hover:bg-[#d11a7d] transition-colors"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <input
                        type={step === 'registro-email' ? 'email' : step === 'registro-telefono' ? 'tel' : 'text'}
                        placeholder={
                          step === 'registro-curp'
                            ? 'CURP (18 caracteres)'
                            : step === 'registro-nombre'
                              ? 'Nombre completo'
                              : step === 'registro-direccion'
                                ? 'Dirección completa'
                                : step === 'registro-email'
                                  ? 'correo@ejemplo.com'
                                  : 'Teléfono (10 dígitos)'
                        }
                        className="flex-1 px-4 py-2 bg-gray-100 rounded-full text-sm outline-none focus:ring-2 focus:ring-[#e91e8c]"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleFormSubmit((e.target as HTMLInputElement).value);
                            (e.target as HTMLInputElement).value = '';
                          }
                        }}
                      />
                      <button
                        onClick={(e) => {
                          const input = (e.currentTarget.previousSibling as HTMLInputElement);
                          handleFormSubmit(input.value);
                          input.value = '';
                        }}
                        className="p-2 bg-[#e91e8c] text-white rounded-full hover:bg-[#d11a7d] transition-colors"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
        </div>
      )}
    </>
  );
}
